/*
  clikt. — fulfilment choice and freight figures, client side.

  Two surfaces share this file:

    1. Product pages. Pickup or Delivery under the options, and if Delivery,
       a postcode that turns into the real freight figure for whichever
       variant is selected right now.
    2. The configurator, where the same choice is mandatory before add to
       cart because a configured build has no other place to ask.

  Rules that shaped it:

    Checkout is the source of truth. Everything here sets expectations and
    writes cart attributes. It never blocks a standard product page.

    We do not invent transit times. We know when a thing leaves the workshop
    and nothing more, so a delivery date is never shown, only a dispatch one.

    WA, NT and TAS have no published figure. They get a person, not a number.
    (No longer true — every state is priced. Kept for the history.)

  All numbers come from snippets/clikt-rates.liquid. Nothing is hardcoded here,
  and that file is itself generated from the freight service — see its header.

  22 Aug 2026: freight is priced by POSTCODE, not just by state. Bendigo is not
  Melbourne and Karratha is not Perth. Every zone carries two rate rows, metro
  and regional, and the capital-city postcode ranges that separate them.
*/
(function () {
  'use strict';

  var STORE_KEY = 'clikt.postcode';
  var SUBURB_KEY = 'clikt.suburb';
  var SUBURB_API = 'https://app.clikt.com.au/freight/suburbs';
  var rates = null;
  var suburbCache = {};

  function data() {
    if (rates) return rates;
    var el = document.getElementById('clikt-rates-json');
    if (!el) return null;
    try { rates = JSON.parse(el.textContent); } catch (e) { rates = null; }
    return rates;
  }

  function money(n) {
    return '$' + Number(n).toLocaleString('en-AU');
  }

  /* --- postcode ---------------------------------------------------------- */

  function stateFor(postcode) {
    var r = data();
    if (!r) return null;
    var n = parseInt(postcode, 10);
    if (!/^\d{4}$/.test(String(postcode).trim()) || isNaN(n)) return null;
    for (var st in r.postcodes) {
      var spans = r.postcodes[st];
      for (var i = 0; i < spans.length; i++) {
        if (n >= spans[i][0] && n <= spans[i][1]) return st;
      }
    }
    return null;
  }

  function zoneFor(state) {
    var r = data();
    if (!r || !state) return null;
    for (var i = 0; i < r.zones.length; i++) {
      if (r.zones[i].states.indexOf(state) > -1) return r.zones[i];
    }
    return null;
  }

  /*
    Which band an order sits in. Weight is the only dial Shopify gives us,
    so it is the one we read: it is what actually decides the charge.
  */
  /* which band an order's nominal weight lands in — see freight.js: every
     mix of up to three carts has its own band, priced per cart */
  function bandFor(grams) {
    var r = data();
    if (!r) return 1;
    for (var i = 0; i < r.bands.length; i++) if (grams <= r.bands[i]) return i;
    return r.bands.length;
  }
  function cartsFor(band) {
    var r = data();
    var bc = (r && r.bandCarts) || [];
    return bc[Math.min(band, bc.length - 1)] || [0, 0];
  }
  function bandPrice(rates, band) {
    var c = cartsFor(band);
    return (c[0] + c[1]) === 0 ? rates[0] : c[0] * rates[1] + c[1] * rates[2];
  }

  /*
    Metro or regional. Metro is the capital-city postcode ranges the zone
    carries; anything else inside the state is regional. This is the whole
    reason the tool asks for a postcode rather than a state.
  */
  function areaFor(zone, postcode) {
    var n = parseInt(postcode, 10);
    var spans = (zone && zone.metroRanges) || [];
    for (var i = 0; i < spans.length; i++) {
      if (n >= spans[i][0] && n <= spans[i][1]) return 'metro';
    }
    return 'regional';
  }

  function priceFor(postcode, grams) {
    var state = stateFor(postcode);
    if (!state) return { error: 'invalid' };
    var zone = zoneFor(state);
    if (!zone) return { error: 'invalid' };
    if (zone.quote) return { state: state, zone: zone, quote: true };
    var area = areaFor(zone, postcode);
    var band = bandFor(grams);
    var carts = cartsFor(band);
    var amount = bandPrice(area === 'metro' ? zone.metro : zone.regional, band);
    return {
      state: state,
      zone: zone,
      area: area,
      band: band,
      carts: carts[0] + carts[1],
      amount: amount,
      /*
        NT and Tasmania are one figure statewide, so "regional" there is a
        fact about the address, not a different price. Only say regional out
        loud where it actually costs more, or the note reads as a surcharge
        that was not applied.
      */
      regionalPriced: area === 'regional' && bandPrice(zone.regional, band) !== bandPrice(zone.metro, band),
    };
  }

  var STATE_NAMES = {
    NSW: 'New South Wales', ACT: 'the ACT', VIC: 'Victoria', QLD: 'Queensland',
    SA: 'South Australia', WA: 'Western Australia', TAS: 'Tasmania', NT: 'the Northern Territory'
  };

  /* --- estimated dispatch ---------------------------------------------- */

  /*
    ✓ Dale 27 Sep 2026: "Estimated Delivery" is now "Estimated dispatch" — "i want to remove 'delivery' because it
    insinuates thats when it arrives to the customer". The date is the day it leaves the workshop (the last build
    day), the same for pickup and delivery. No travel days are added any more, so the postcode no longer moves it.
  */
  function updateDelivery(state, method) {
    var el = document.querySelector('[data-clikt-deliver]');
    if (!el) return;
    el.textContent = el.getAttribute('data-ready') || '';
  }

  /* --- remembered postcode ---------------------------------------------- */

  function savedPostcode() {
    try { return window.localStorage.getItem(STORE_KEY) || ''; } catch (e) { return ''; }
  }

  function savePostcode(pc) {
    try { window.localStorage.setItem(STORE_KEY, pc); } catch (e) { /* private mode */ }
  }

  function savedSuburb() {
    try { return window.localStorage.getItem(SUBURB_KEY) || ''; } catch (e) { return ''; }
  }

  function saveSuburb(name) {
    try { window.localStorage.setItem(SUBURB_KEY, name); } catch (e) { /* private mode */ }
  }

  /*
    Suburbs for a postcode, from our own list on app.clikt.com.au. Cached per
    page so retyping the same number does not go back out to the network, and
    a failure is silent: the price still works off the postcode alone, so a
    dropped request costs a nicety rather than a sale.
  */
  /* ✓ Dale 26 Aug: a NAME search over the same index — each match carries its
     postcode, so picking one simply re-enters the postcode flow. */
  /* the whole index, fetched once — lookups go local and instant after it
     lands (Dale 26 Aug: "populating as you type") */
  var fullIndex = null, fullLoading = null;
  function preloadSuburbs() {
    if (fullIndex) return Promise.resolve(fullIndex);
    if (fullLoading) return fullLoading;
    fullLoading = fetch(SUBURB_API + '?all=1')
      .then(function (r) { return r.json(); })
      .then(function (d) { fullIndex = d && !d.matches ? d : null; return fullIndex; })
      .catch(function () { fullLoading = null; return null; });
    return fullLoading;
  }
  /* ✓ Dale 25 Sep 2026: "as you type, the list narrows to available selections" — every match
     is ranked: starts-with before contains, Victoria before the other states, then A–Z; up to
     40 rows (the list scrolls). The old 12-row A–Z cut buried Clifton Hill under NSW names. */
  var STATE_RANK = { VIC: 0, NSW: 1, SA: 2, ACT: 3, QLD: 4, TAS: 5, WA: 6, NT: 7 };
  function localSearch(q) {
    var hits = [];
    for (var code in fullIndex) {
      var rows = fullIndex[code];
      for (var i = 0; i < rows.length; i++) {
        var n = rows[i][0].toLowerCase(), at = n.indexOf(q);
        if (at === -1) continue;
        var sr = STATE_RANK[rows[i][1]]; if (sr == null) sr = 8;
        hits.push({ name: rows[i][0], state: rows[i][1], postcode: code, r: (at === 0 ? 0 : 10) + sr });
      }
    }
    hits.sort(function (x, y) { return x.r - y.r || x.name.localeCompare(y.name) || x.postcode.localeCompare(y.postcode); });
    return hits.slice(0, 40).map(function (h) { return { name: h.name, state: h.state, postcode: h.postcode }; });
  }
  var nameCache = {};
  function searchSuburbs(q) {
    q = String(q || '').trim().toLowerCase();
    if (q.length < 2) return Promise.resolve([]);
    if (fullIndex) return Promise.resolve(localSearch(q));
    preloadSuburbs();
    if (nameCache[q]) return Promise.resolve(nameCache[q]);
    return fetch(SUBURB_API + '?q=' + encodeURIComponent(q))
      .then(function (r) { return r.json(); })
      .then(function (d) { nameCache[q] = d.matches || []; return nameCache[q]; })
      .catch(function () { return []; });
  }
  function lookupSuburbs(postcode) {
    if (fullIndex && fullIndex[postcode]) {
      return Promise.resolve(fullIndex[postcode].map(function (r) { return { name: r[0], state: r[1] }; }));
    }
    preloadSuburbs();
    if (suburbCache[postcode]) return Promise.resolve(suburbCache[postcode]);
    return fetch(SUBURB_API + '?postcode=' + encodeURIComponent(postcode))
      .then(function (r) { return r.json(); })
      .then(function (d) {
        suburbCache[postcode] = d.suburbs || [];
        return suburbCache[postcode];
      })
      .catch(function () { return []; });
  }

  /*
    Cart attributes. These ride into the order so the workshop knows whether
    someone is collecting before anyone has to ask.
  */
  function writeCartAttributes(method, postcode, suburb) {
    var attrs = {
      Fulfilment: method || '',
      'Delivery postcode': postcode || '',
      'Delivery suburb': suburb || '',
    };
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () { /* nothing the shopper needs to see */ });
  }

  /* --- the freight quote hand-off --------------------------------------- */

  function quoteUrl(params) {
    var q = new URLSearchParams();
    q.set('enq', 'freight');
    Object.keys(params).forEach(function (k) {
      if (params[k]) q.set(k, params[k]);
    });
    return '/pages/contact?' + q.toString();
  }

  /* --- product page ------------------------------------------------------ */

  function initProduct(root) {
    var weights = {};
    var wEl = root.querySelector('[data-clikt-weights]');
    if (wEl) { try { weights = JSON.parse(wEl.textContent); } catch (e) { weights = {}; } }

    var form = document.querySelector('form[action*="/cart/add"]');
    var pcInput = root.querySelector('[data-clikt-pc]');
    var suburbBox = root.querySelector('[data-clikt-suburbs]');
    var out = root.querySelector('[data-clikt-out]');
    var deliveryPane = root.querySelector('[data-clikt-pane="delivery"]');
    var pickupPane = root.querySelector('[data-clikt-pane="pickup"]');
    var buttons = root.querySelectorAll('[data-clikt-method]');
    var method = '';
    var chosenSuburb = '';

    function currentVariantId() {
      var input = form && form.querySelector('input[name="id"]');
      return input ? String(input.value) : root.getAttribute('data-default-variant');
    }

    function currentGrams() {
      var id = currentVariantId();
      return weights[id] != null ? weights[id] : 60000;
    }

    /*
      Refresh the estimated-delivery line from wherever the shopper is now:
      their state if a full postcode is in, else the common-case band.
    */
    function refreshDelivery() {
      var pc = (pcInput && pcInput.value || '').trim();
      var st = pc.length === 4 ? stateFor(pc) : null;
      updateDelivery(st, method);
    }

    function variantLabel() {
      var sel = form && form.querySelector('input[name="id"]');
      if (!sel) return '';
      var opt = root.getAttribute('data-variant-map');
      if (!opt) return '';
      try { return (JSON.parse(opt)[String(sel.value)] || ''); } catch (e) { return ''; }
    }

    /*
      The dispatch line. One date, and a label that changes with the choice,
      because "delivered by" would be a promise about a truck we have not
      booked yet.
    */
    function syncDispatchLabel() {
      var label = document.querySelector('[data-clikt-dispatch-label]');
      if (!label) return;
      if (method === 'Pickup') label.textContent = label.getAttribute('data-pickup');
      else if (method === 'Delivery') label.textContent = label.getAttribute('data-delivery');
      else label.textContent = label.getAttribute('data-idle');
    }

    /* --- the suburb step ------------------------------------------------ */

    function clearSuburbs() {
      if (suburbBox) suburbBox.innerHTML = '';
    }

    function showSuburbs(list) {
      if (!suburbBox) return;
      if (!list.length) { collapsePill(false); suburbBox.hidden = true; return; }

      /*
        One suburb for a postcode is not a choice, it is a fact. Confirm it
        and move on rather than making someone click their only option.
      */
      if (list.length === 1) {
        pick(list[0].name);
        return;
      }

      var html = '';
      list.forEach(function (s) {
        html += '<button type="button" class="clikt-drop__item" data-suburb="' +
          s.name.replace(/"/g, '&quot;') + '">' + s.name + '</button>';
      });
      suburbBox.innerHTML = html;
      suburbBox.hidden = false;
      spin(false);
    }

    /* --- the pill -------------------------------------------------------- */

    var pill = root.querySelector('[data-clikt-pill]');
    var resolvedEl = root.querySelector('[data-clikt-resolved]');
    var resultEl = root.querySelector('[data-clikt-result]');
    var changeBtn = root.querySelector('[data-clikt-change]');
    var spinner = root.querySelector('[data-clikt-spinner]');

    function spin(on) {
      if (spinner) spinner.hidden = !on;
      if (pill) pill.classList.toggle('is-busy', !!on);
    }

    /*
      The whole answer lands in the pill, not under it: the postcode and
      suburb on the left, and on the right first the two-second think, then
      the freight number in its place. One control, one line, one result.
    */
    var THINK = '<span class="clikt-fulfil__dots" aria-hidden="true"><i></i><i></i><i></i></span>';

    function setResult(html) {
      if (!resultEl) return;
      resultEl.classList.remove('is-think');
      if (!html) { resultEl.hidden = true; resultEl.innerHTML = ''; return; }
      resultEl.hidden = false;
      resultEl.innerHTML = html;
    }

    function showThinking() {
      if (!resultEl) return;
      resultEl.hidden = false;
      resultEl.classList.add('is-think');
      resultEl.innerHTML = THINK;
    }

    /*
      Collapsed, the pill IS the answer: "3056 · Brunswick Lower". The field
      only needs to exist while it is being filled in, and leaving it there
      afterwards invites a second go at a question already answered.
    */
    function collapsePill(done) {
      if (!pill) return;
      pill.classList.toggle('is-resolved', !!done);
      if (pcInput) pcInput.hidden = !!done;
      if (resolvedEl) resolvedEl.hidden = !done;
      if (changeBtn) changeBtn.hidden = !done;
      if (!done) setResult('');
    }

    function reopenPill() {
      chosenSuburb = '';
      saveSuburb('');
      if (suburbBox) { suburbBox.innerHTML = ''; suburbBox.hidden = true; }
      if (out) out.innerHTML = '';
      collapsePill(false);
      spin(false);
      if (pcInput) { pcInput.value = ''; pcInput.focus(); }
    }

    if (changeBtn) changeBtn.addEventListener('click', function (e) { e.preventDefault(); reopenPill(); });

    function pick(name) {
      chosenSuburb = name;
      saveSuburb(name);
      if (suburbBox) { suburbBox.innerHTML = ''; suburbBox.hidden = true; }

      var pc = (pcInput && pcInput.value || '').trim();
      var res = priceFor(pc, currentGrams());
      if (resolvedEl) {
        resolvedEl.textContent = pc + '  ·  ' + name + (res && res.state ? ' ' + res.state : '');
      }
      collapsePill(true);
      spin(false);
      writeCartAttributes(method, pc, chosenSuburb);

      /*
        A deliberate pause before the number lands, right where the number
        will be.

        The figure is worked out on this machine and appears instantly, which
        reads as a canned answer rather than a quote. Two seconds of the pill
        visibly thinking is what makes the price feel calculated for the
        address just given.
      */
      if (out) out.innerHTML = '';
      showThinking();

      window.clearTimeout(pick._t);
      pick._t = window.setTimeout(function () {
        render();
      }, 2000);
    }

    if (suburbBox) {
      suburbBox.addEventListener('click', function (e) {
        var b = e.target.closest('[data-suburb]');
        /* a NAME match carries its postcode: fill it and re-run the flow */
        if (b && b.getAttribute('data-postcode')) {
          e.preventDefault();
          saveSuburb(b.getAttribute('data-suburb'));
          if (pcInput) pcInput.value = b.getAttribute('data-postcode');
          onPostcode();
          return;
        }
        if (!b) return;
        e.preventDefault();
        pick(b.getAttribute('data-suburb'));
      });
    }

    function onPostcode() {
      var pc = (pcInput && pcInput.value || '').trim();
      chosenSuburb = '';
      clearSuburbs();
      if (suburbBox) suburbBox.hidden = true;

      /* ✓ Dale 26 Aug: letters search by SUBURB NAME; the pick fills the
         postcode and falls straight back into the flow below. */
      var letters = pc.replace(/[^A-Za-z ]/g, '').trim();
      var digits = pc.replace(/\D/g, '');
      if (digits.length < 4 && letters.length >= 2) {
        spin(true); out.innerHTML = '';
        searchSuburbs(letters).then(function (m) {
          if ((pcInput.value || '').trim() !== pc) return;
          spin(false);
          if (!m.length) { render(); return; }
          var html = '';
          m.forEach(function (s) {
            html += '<button type="button" class="clikt-drop__item" data-suburb="' +
              s.name.replace(/"/g, '&quot;') + '" data-postcode="' + s.postcode + '">' +
              s.name + ' <span style="opacity:.55">' + s.state + ' ' + s.postcode + '</span></button>';
          });
          suburbBox.innerHTML = html;
          suburbBox.hidden = false;
        });
        render();
        return;
      }

      /* Nothing happens until all four digits are in — no half-typed lookups. */
      if (pc.length !== 4) { spin(false); out.innerHTML = ''; render(); return; }

      savePostcode(pc);
      /* Delivery estimate only needs the state, so update it the moment the
         four digits are in — no need to wait for the suburb or the price. */
      refreshDelivery();
      spin(true);
      out.innerHTML = '';

      lookupSuburbs(pc).then(function (list) {
        /* The shopper may have kept typing while that was in the air. */
        if ((pcInput.value || '').trim() !== pc) return;
        if (!list.length) { spin(false); render(); return; }
        var remembered = savedSuburb();
        var match = list.filter(function (s) { return s.name === remembered; });
        if (match.length) { pick(remembered); return; }
        out.innerHTML = '';
        showSuburbs(list);
      });
    }

    /* --- the figure ------------------------------------------------------ */

    function render() {
      refreshDelivery();

      var pc = (pcInput && pcInput.value || '').trim();

      /* Not delivering, or nothing typed: the pill holds no number. */
      if (method !== 'Delivery' || !pc) { setResult(''); if (out) out.innerHTML = ''; return; }

      if (pc.length !== 4) {
        setResult('');
        if (out) out.innerHTML = '<p class="clikt-fulfil__msg clikt-fulfil__msg--warn">' +
          'That postcode doesn\'t look right. Four digits.</p>';
        return;
      }

      var res = priceFor(pc, currentGrams());

      if (res.error) {
        setResult('');
        if (out) out.innerHTML = '<p class="clikt-fulfil__msg clikt-fulfil__msg--warn">' +
          'That postcode doesn\'t look right. Four digits.</p>';
        return;
      }

      /* Nothing is priced until we know where it is going. */
      if (!chosenSuburb) { setResult(''); if (out) out.innerHTML = ''; return; }

      var where = chosenSuburb + ' ' + res.state + ' ' + pc;

      /*
        WA, NT and TAS have no published figure, so there is no number to put
        in the pill. The pill says as much, and the offer to quote it by hand
        sits underneath where there is room for a sentence and a button.
      */
      if (res.quote) {
        setResult('<span class="clikt-pill__byquote">By quote</span>');
        var url = quoteUrl({
          postcode: pc,
          suburb: chosenSuburb,
          product: root.getAttribute('data-product-title'),
          variant: variantLabel()
        });
        if (out) out.innerHTML =
          '<p class="clikt-fulfil__msg">We quote freight to ' + where +
          ' personally, usually within one business day.</p>' +
          '<a class="clikt-fulfil__quote" href="' + url + '">Get a freight quote' +
          '<span aria-hidden="true">&#8594;</span></a>';
        return;
      }

      /* The number, in the pill, in the place the think just vacated. */
      setResult('<strong class="clikt-pill__amt">' + money(res.amount) + '</strong>');
      /*
        Say when a rate is the regional one. A shopper who read "Victoria from
        $195" on the card and is quoted $395 deserves the reason in the same
        breath, not a suspicion that the number moved on them.
      */
      if (out) {
        out.innerHTML = res.regionalPriced
          ? '<p class="clikt-fulfil__msg">Regional rate — ' + chosenSuburb +
            ' is outside the ' + (STATE_NAMES[res.state] || res.state) + ' metro area.</p>'
          : '';
      }
    }

    function choose(next) {
      method = next;
      for (var i = 0; i < buttons.length; i++) {
        var on = buttons[i].getAttribute('data-clikt-method') === next;
        buttons[i].classList.toggle('is-on', on);
        buttons[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      if (pickupPane) pickupPane.hidden = next !== 'Pickup';
      if (deliveryPane) deliveryPane.hidden = next !== 'Delivery';
      syncDispatchLabel();
      if (next === 'Delivery' && pcInput && (pcInput.value || '').length === 4 && !chosenSuburb) {
        onPostcode();
      } else {
        render();
      }
      writeCartAttributes(
        next,
        next === 'Delivery' ? (pcInput && pcInput.value || '').trim() : '',
        next === 'Delivery' ? chosenSuburb : ''
      );
    }

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function (e) {
        e.preventDefault();
        choose(this.getAttribute('data-clikt-method'));
      });
    }

    if (pcInput) {
      pcInput.value = savedPostcode();
      pcInput.addEventListener('input', function () {
        /* ✓ Dale 26 Aug: postcode OR suburb name in the one box. Letters
           pass through to the name search; digits-only keeps the strict
           four-digit clamp it always had. */
        if (/[A-Za-z]/.test(this.value)) {
          this.value = this.value.replace(/[^A-Za-z0-9 ]/g, '').slice(0, 24);
        } else {
          this.value = this.value.replace(/\D/g, '').slice(0, 4);
        }
        onPostcode();
      });
    }

    /*
      Variant changes. Dawn re-renders the price and the buy button but not
      this panel, so the figure would otherwise describe the size someone
      was looking at a moment ago. The double read is deliberate: the hidden
      id input is updated after the change event on some pickers.
    */
    document.addEventListener('change', function (e) {
      if (!e.target.closest('variant-selects, .product-form__input')) return;
      render();
      window.setTimeout(render, 350);
    });

    render();
  }

  /* --- configurator ------------------------------------------------------ */

  /*
    The configurator has no Shopify variant to weigh, so the band comes off
    the size the shopper picked, using the same rule the catalogue weights
    were built with: under 1500mm is a standard cart, 1500 and over is large.
  */
  function gramsForSize(sizeLabel) {
    var label = String(sizeLabel || '');
    /* Sizes read in centimetres now; the configurator's own labels are still mm. */
    var cm = label.match(/(\d{2,4})\s*cm\b/i);
    var m = label.match(/(\d{3,4})\s*mm\b/i);
    var mm = cm ? parseInt(cm[1], 10) * 10 : (m ? parseInt(m[1], 10) : 1400);
    /* nominal weights, the same ones set-shipping-weights.js gives the
       variants: a standard cart is exactly 50kg, a large one exactly 80kg */
    return mm < 1500 ? 50000 : 80000;
  }

  /* ✓ 24 Sep 2026 (Lighthouse): the whole 114 KB suburb index used to download
     the moment this file loaded, on EVERY page, before anyone had touched a
     postcode box. Now it warms on the first focus of any postcode / suburb
     field; until it lands, lookupSuburbs/searchSuburbs fall back to the
     per-postcode and per-name endpoints, exactly as they always did. */
  document.addEventListener('focusin', function warmOnFirstTouch(e) {
    var t = e.target;
    if (!t || !t.matches) return;
    if (t.matches('[data-clikt-pc], [data-clikt-cart-pc], input[name="postcode"], input[autocomplete="postal-code"], .clikt-fulfil input, [data-clikt-fulfil] input, input[placeholder*="ostcode"], input[placeholder*="uburb"]')) {
      preloadSuburbs();
      document.removeEventListener('focusin', warmOnFirstTouch);
    }
  });
  window.CliktFulfil = {
    data: data,
    stateFor: stateFor,
    zoneFor: zoneFor,
    bandFor: bandFor,
    priceFor: priceFor,
    money: money,
    stateNames: STATE_NAMES,
    savedPostcode: savedPostcode,
    savePostcode: savePostcode,
    writeCartAttributes: writeCartAttributes,
    quoteUrl: quoteUrl,
    gramsForSize: gramsForSize,
    /* the configurator asks its own suburb question, off the same list */
    lookupSuburbs: lookupSuburbs,
    searchSuburbs: searchSuburbs,
    savedSuburb: savedSuburb,
    saveSuburb: saveSuburb
  };

  function boot() {
    var roots = document.querySelectorAll('[data-clikt-fulfil]');
    for (var i = 0; i < roots.length; i++) initProduct(roots[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
